import { QueryClient } from '@tanstack/react-query'
import { hasTag, removeTag } from './notificationTracker'
import { SupabaseNote } from './types'
import {
  REALTIME_SUBSCRIBE_STATES,
  RealtimePostgresChangesPayload,
} from '@supabase/supabase-js'
import {
  cacheKeyFromNoteType,
  cacheKeys,
  noteQueryKey,
} from '../../utils/queryKeyFactory'
import { Note } from '../../utils/note'
import { supabaseClient } from '../SupabaseClient'
import { loadNoteFrom } from './noteUtils'
import { AuthenticatedUser } from '../../utils/User'
import {
  removeFromCacheByRecordNames,
  updateCache,
} from '../../contexts/CachedNotes'

export const notesChannel = 'notes'

export function subscribeToChannel(
  user: AuthenticatedUser,
  queryClient: QueryClient,
  cachedNotesQueryClient: QueryClient,
  setIsRegistered: (isRegistered: boolean) => void
) {
  supabaseClient
    .channel(notesChannel)
    .on<SupabaseNote>(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'notes' },
      (payload) => {
        handleChange(payload, user, queryClient, cachedNotesQueryClient).catch(
          (error: unknown) => {
            console.error('Error handling change:', error)
            setIsRegistered(false)
          }
        )
      }
    )
    .subscribe((status) => {
      // console.debug('Supabase connection status:', status)
      if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
        setIsRegistered(true)
      } else {
        setIsRegistered(false)
      }
    })
}

async function handleChange(
  payload: RealtimePostgresChangesPayload<SupabaseNote>,
  user: AuthenticatedUser,
  queryClient: QueryClient,
  cachedNotesQueryClient: QueryClient
) {
  const id =
    'change_tag' in payload.new
      ? payload.new.change_tag
      : 'id' in payload.old
        ? payload.old.id
        : undefined
  if (id && hasTag(id)) {
    removeTag(id)
    return
  }

  const currentUserId = 'teamUserId' in user ? user.teamUserId : user.userId

  // eslint-disable-next-line no-console
  console.debug('note changed', payload)
  switch (payload.eventType) {
    case 'INSERT':
    case 'UPDATE': {
      const updatedNote = await loadNoteFrom(currentUserId, payload.new)
      queryClient.setQueryData<Note>(
        noteQueryKey(updatedNote),
        () => updatedNote
      )

      const cacheKey = cacheKeyFromNoteType(updatedNote.noteType, user)
      updateCache(cachedNotesQueryClient, [updatedNote], cacheKey)
      break
    }
    case 'DELETE': {
      // EDIT: This stopped removing the teamspace note from the sidebar, probably because the wrong cacheKey was used or so
      // Now covering more cacheKeys

      // Remove from all queries that match the record name
      const recordId = payload.old.id
      if (recordId) {
        const allQueries = queryClient.getQueriesData<Note>(['notes'])
        for (const [queryKey, note] of allQueries) {
          if (note?.recordName === recordId) {
            queryClient.removeQueries(queryKey)
          }
        }

        // Remove from caches
        removeFromCacheByRecordNames(
          cachedNotesQueryClient,
          [recordId],
          cacheKeys.teamNotes(currentUserId)
        )
        removeFromCacheByRecordNames(
          cachedNotesQueryClient,
          [recordId],
          cacheKeys.privateNotes(currentUserId)
        )
        removeFromCacheByRecordNames(
          cachedNotesQueryClient,
          [recordId],
          cacheKeys.teamProjectNotes(currentUserId)
        )
        removeFromCacheByRecordNames(
          cachedNotesQueryClient,
          [recordId],
          cacheKeys.privateProjectNotes(currentUserId)
        )
      }
      break
    }
  }
}
